import axios, { AxiosRequestTransformer, AxiosResponseTransformer, InternalAxiosRequestConfig } from 'axios';
import noop from 'lodash-es/noop';
import qs, { IStringifyOptions } from 'qs';

import { CSRF_COOKIE_NAME, UI_TIMEOUT, UNIFIED_VERSION, URLS } from '@/constants';
import { env } from '@/constants/env';
import notify from '@/notify';
import { useAppConfigStore } from '@/piniaStores/appConfig/appConfig.store';
import { useUserStore } from '@/piniaStores/user/user.store';
import i18n from '@/plugins/i18n';
import { getAuth0Client } from '@/services/auth';
import { isHttpError } from '@/utils/errors';
import getIntegrationsServiceUrl from '@/utils/getIntegrationsServiceUrl';
import JSONTransform from '@/utils/json-transform';

import * as requestManager from './request-manager';

const CSRF_HEADER_NAME = 'X-CSRFToken';

export const INVALID_TOKEN_ERROR_CODE = 'invalid.token';
// https://www.django-rest-framework.org/tutorial/4-authentication-and-permissions/#authenticating-with-the-api
const NOT_AUTHENTICATED_DETAIL = 'Authentication credentials were not provided.';
// https://github.com/encode/django-rest-framework/blob/master/rest_framework/exceptions.py#L176
const NOT_AUTHENTICATED_CODE = 'not_authenticated';

export interface CreateSessionOptions {
  shouldTransform?: boolean;
  onUploadProgress?: (progress: number) => void;
  unified?: boolean;
  includeBearerToken?: boolean;
  arrayFormat?: IStringifyOptions['arrayFormat'];
  skipAuthRedirect?: boolean;
}

const DEFAULT_OPTIONS: CreateSessionOptions = {
  shouldTransform: true,
  onUploadProgress: noop,
  unified: false,
  includeBearerToken: false,
  arrayFormat: 'repeat',
  skipAuthRedirect: false,
};

/**
 * Function that generates a session object
 * @param {string} baseUrl - url for axios instance
 * @param {object} userOptions - set of options for building axios instance
 * @param {boolean} userOptions.shouldTransform - whether or not to transform from/to snake_case to/from camelCase
 * @param {function} userOptions.onUploadProgress - function to be called when the upload progress value updates
 */
export function createSession(baseUrl: string | (() => string), userOptions?: CreateSessionOptions) {
  const options = { ...DEFAULT_OPTIONS, ...userOptions };
  const session = axios.create({
    xsrfCookieName: CSRF_COOKIE_NAME,
    xsrfHeaderName: CSRF_HEADER_NAME,
    ...(options.shouldTransform && {
      transformRequest: [
        (data) => new JSONTransform(data).toSnakeCaseKeys(),
        ...((axios.defaults.transformRequest as AxiosRequestTransformer[]) || []),
      ],
      transformResponse: [
        ...((axios.defaults.transformResponse as AxiosResponseTransformer[]) || []),
        (response) => (response ? new JSONTransform(response).toCamelCaseKeys() : response),
      ],
    }),
    ...(options.arrayFormat && {
      paramsSerializer: (params) => {
        return qs.stringify(params, {
          arrayFormat: options.arrayFormat,
        });
      },
    }),
    onUploadProgress: (progressEvent) => {
      if (!progressEvent.total || !options.onUploadProgress) {
        return;
      }

      const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);

      options.onUploadProgress(progress);
    },
  });

  /**
   * Removes completed requests from request manager
   */
  function registerRequest(config: InternalAxiosRequestConfig): InternalAxiosRequestConfig {
    const { requestId } = config;

    if (requestId) {
      const controller = new AbortController();

      config.signal = controller.signal;
      requestManager.add(requestId, controller);
    }

    return config;
  }

  /**
   * Removes registered, completed non-batch requests from request manager
   * Note: Batch request cancellation should be managed separately
   */
  function unregisterRequest(config: InternalAxiosRequestConfig): InternalAxiosRequestConfig {
    const { requestId } = config;

    if (requestId && !requestManager.isBatch(requestId)) {
      requestManager.remove(requestId);
    }

    return config;
  }

  function getBaseURL() {
    const parsedBaseString = typeof baseUrl === 'function' ? baseUrl() : baseUrl;

    if (env.MODE === 'test' && !parsedBaseString.startsWith('http')) {
      return `${URLS.ORIGIN_TEST}${parsedBaseString}`;
    }

    if (options.unified) {
      // NOTE: As the Unified API gateway expands, we might need to include a passed baseUrl param
      return useAppConfigStore().unifiedApiUrl;
    }

    return parsedBaseString;
  }

  // https://axios-http.com/docs/interceptors
  session.interceptors.request.use(async (config) => {
    config.baseURL = getBaseURL();

    // methods to act on request before it's sent
    if (options.unified || options.includeBearerToken) {
      const accessToken = await getAuth0Client()
        ?.getTokenSilently()
        .catch(() => Promise.reject(INVALID_TOKEN_ERROR_CODE));

      config.headers = config.headers || {};
      config.headers['Authorization'] = `Bearer ${accessToken}`;
      config.headers['LeafLink-Version'] = UNIFIED_VERSION.v1;
    }

    return registerRequest(config);
  });

  // https://axios-http.com/docs/interceptors
  session.interceptors.response.use(
    (response) => {
      const config = unregisterRequest(response.config);

      return Object.assign(response, { config });
    },
    (error) => {
      const isUnauthenticatedApiResponse = (() => {
        if (!isHttpError(error) || !error.response) {
          return false;
        }

        if (error.response.status === 401) {
          return true;
        }

        if (!error.response.data || typeof error.response.data !== 'object') {
          return false;
        }

        if ('detail' in error.response.data && error.response.data.detail === NOT_AUTHENTICATED_DETAIL) {
          return true;
        }

        if (
          'errors' in error.response.data &&
          Array.isArray(error.response.data.errors) &&
          error.response.data.errors.find((e) => e.code === NOT_AUTHENTICATED_CODE)
        ) {
          return true;
        }

        return false;
      })();

      let timeout: number | undefined;
      if (options.skipAuthRedirect) {
        // clearing timeouts so no redirect happens because some other unauthorized request that doesn't want to skipAuthRedirect.
        window.clearTimeout(timeout);
      } else if (error === INVALID_TOKEN_ERROR_CODE || isUnauthenticatedApiResponse) {
        notify.warning(i18n.t('notify.errorNotAuthenticatedRedirect'));

        timeout = window.setTimeout(() => {
          window.location.replace('/accounts/login/?next=/');
        }, UI_TIMEOUT);
      }

      if (error.config?.requestId) {
        unregisterRequest(error.config);
      }

      if (error.message !== 'canceled') {
        return Promise.reject(error);
      }
    },
  );

  return session;
}

/**
 * Generate internal session base url with company slug
 * @TODO Set as default after removing all 'params.company_slug'
 */
export const apiSlugSession = createSession(() => {
  const userStore = useUserStore();

  return URLS.INTERNAL + userStore.userActiveCompany?.slug;
});

/**
 * Generate session with /c/company_slug as the base url
 */
export const slugSession = createSession(() => {
  const userStore = useUserStore();

  return `/c/${userStore.userActiveCompany?.slug}`;
});

/**
 * Generate session to interact with the Unified API
 * @deprecated Use only generated endpoint logic instead
 */
export const unifiedSession = createSession('', { unified: true });

/**
 * Generate a session to interact with the connections (integrations) service
 */
export const integrationsSession = createSession(getIntegrationsServiceUrl(), {
  shouldTransform: false,
  includeBearerToken: true,
});

/**
 * Generate default session with base api url and transform request and response
 */
export const apiSession = createSession(URLS.INTERNAL);

export default apiSession;
